import { Container } from '@mui/material'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import React from 'react'
import { addApolloState, initializeApollo } from '../../lib/apolloClient'
import { CategoryPage } from '../../src/components/Modules'
import NotFound from '../../src/components/Modules/NotFound'
import Layout from '../../src/constants/Layout'
import { AppContextWrapper } from '../../src/context/appContext'
import { CategoryContextWrapper } from '../../src/context/pagenumberContext'
import {
  GET_CATEGORY_DATA,
  GET_CHILD_CATEGORIES,
  GET_PARENT_CATEGORIES,
  GET_PARENT_CATEGORIES_NON_RECURSIVE,
  GET_SORTED_BY_POSITION_PRODUCTS,
} from '../../src/operations/queries'
import { GET_CATEGORY_PERMALINKS } from '../../src/operations/queries/sitemapQueries'
import { APP_SHOP_SETTINGS } from '../../src/operations/queries/_appQueries'
import { sortCategories } from '../../src/utilityFunctions/categoryUtils'
import { getProfileIndexFromLanguage } from '../../src/utilityFunctions/getProfile'
import { categoryMetaTags } from '../../src/utilityFunctions/metaUtils/categoryMetaTags'
import { useShopSettingsInit } from '../../src/utilityFunctions/useShopSettingsInit'

const Category = ({
  currentCategoryData,
  staticData,
  categoriesData,
  childCategoriesData,
  categoryName,
  categoryDescription,
  permalink,
  fullPermalink,
  shopSettingsData,
  pageNumber,
  locale,
  locales,
  defaultLocale,
  _nextI18Next,
  ...props
}) => {
  const { t } = useTranslation(['common'])
  const categorySlug = t('category-slug')
  const productSlug = t('product-slug')
  const { initialI18nStore } = _nextI18Next
  useShopSettingsInit(shopSettingsData.data.findFirstShopSetting)
  const shopSettings = { ...shopSettingsData.data.findFirstShopSetting }

  // ========= Return 404 if no data is found...
  if (!currentCategoryData?.data?.findUniqueMetaInformation) {
    return (
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <Layout categoriesData={categoriesData} locale={locale}>
          <Container maxWidth="xl">
            <NotFound />
          </Container>
        </Layout>
      </AppContextWrapper>
    )
  }

  const productsCount =
    staticData.data.sortedByCategoryPositionProductContainers.productsCount

  const productContainers =
    staticData.data.sortedByCategoryPositionProductContainers
      .sortedProductContainers

  // ===== USER AGENT ==== information about incoming user
  // We use this info to determine if its Safari (as it doesnt support webp images)
  let ua
  // if (props.uaString) {
  //   ua = parse(props.uaString);
  // } else {
  //   ua = parse(window.navigator.userAgent);
  // }

  return (
    <>
      {categoryMetaTags({
        currentCategoryData,
        locale,
        locales,
        shopSettingsData,
        productsCount,
        pageNumber,
        categorySlug,
        defaultLocale,
        initialI18nStore,
        productSlug,
        productContainers,
      })}
      <AppContextWrapper locale={locale} shopSettings={shopSettings}>
        <CategoryContextWrapper data={{ staticData }} locale={locale}>
          <Layout categoriesData={categoriesData} locale={locale}>
            <CategoryPage
              staticData={
                staticData.data.sortedByCategoryPositionProductContainers
                  .sortedProductContainers
              }
              childCategoriesData={childCategoriesData}
              categoryName={categoryName}
              categoryDescription={categoryDescription}
              permalink={permalink}
              fullPermalink={fullPermalink}
              productsCount={
                staticData.data.sortedByCategoryPositionProductContainers
                  .productsCount
              }
              numberOfPages={
                staticData.data.sortedByCategoryPositionProductContainers
                  .numberOfPages
              }
              pageNumber={pageNumber}
              categoryId={
                currentCategoryData.data.findUniqueMetaInformation
                  .category_profile.category.id
              }
            />
          </Layout>
        </CategoryContextWrapper>
      </AppContextWrapper>
    </>
  )
}

const language = process.env.DEFAULT_LOCALE
const itemsPerPage = process.env.NEXT_PUBLIC_ITEMS_PER_CATEGORY_PAGE
  ? parseInt(process.env.NEXT_PUBLIC_ITEMS_PER_CATEGORY_PAGE)
  : 32

//* ==================================================================================================
//* ======================================= GET STATIC PATHS =========================================
//* ==================================================================================================
export const getStaticPaths: GetStaticPaths = async (ctx) => {
  const apolloClient = initializeApollo()
  // ==============================    TESTING SPACE ===========================

  // ================== CATEGORY =============================
  // --- GET CATEGORY PERMALINKS  FROM BACKEND
  const categoryPermalinksQuery = await apolloClient.query({
    query: GET_CATEGORY_PERMALINKS,
    variables: {
      language: language,
    },
  })

  // ====
  // Categories have to be nested by child / parent relation ship
  // Categories have to be with a page number 1,2,3 at the end...
  let categoryPermalinks = [] as any
  for (const item of categoryPermalinksQuery.data.findManyCategory) {
    // We are looping each item...

    const currentCategoryPages = Math.ceil(
      item.product_containers.length / itemsPerPage,
    )

    const maxCategoryPagesGenerated = 5

    const generateNumberOfCategoryPages =
      currentCategoryPages > maxCategoryPagesGenerated
        ? maxCategoryPagesGenerated
        : currentCategoryPages

    const categoryProfileIndex: number = getProfileIndexFromLanguage(
      item.category_profiles,
      language,
    )
    const permalink =
      item.category_profiles[categoryProfileIndex]?.meta_information?.permalink

    for (let i = 1; i <= generateNumberOfCategoryPages; i++) {
      const fullLink = `${permalink}/${i}`
      const sitemapObject = {
        loc: fullLink, // Absolute url
        lastmod: item.updatedAt,
        changefreq: 'daily',
        priority: 0.7,
      }

      categoryPermalinks.push(sitemapObject)
    }

    if (item.child_categories.length > 0) {
      // ==== LEVEL 1 CHILDREN =========
      const childListt_1 = item.child_categories
      const parentPermalink_1 = permalink
      for (const c_item of childListt_1) {
        // We are looping each item...

        const currentCategoryPages = Math.ceil(
          c_item.product_containers.length / itemsPerPage,
        )
        const generateNumberOfCategoryPages =
          currentCategoryPages > maxCategoryPagesGenerated
            ? maxCategoryPagesGenerated
            : currentCategoryPages

        const categoryProfileIndex: number = getProfileIndexFromLanguage(
          c_item.category_profiles,
          language,
        )
        const permalink =
          c_item.category_profiles[categoryProfileIndex]?.meta_information
            ?.permalink
        for (let i = 1; i <= generateNumberOfCategoryPages; i++) {
          const fullLink = `${parentPermalink_1}/${permalink}/${i}`
          const sitemapObject = {
            loc: fullLink, // Absolute url
            lastmod: c_item.updatedAt,
            changefreq: 'daily',
            priority: 0.7,
          }
          categoryPermalinks.push(sitemapObject)
        }

        if (c_item.child_categories.length > 0) {
          // ==== LEVEL 2 CHILDREN =========
          const childListt_2 = c_item.child_categories
          const parentPermalink_2 = permalink
          for (const c_item_2 of childListt_2) {
            // We are looping each item...

            const currentCategoryPages = Math.ceil(
              c_item_2.product_containers.length / itemsPerPage,
            )
            const generateNumberOfCategoryPages =
              currentCategoryPages > maxCategoryPagesGenerated
                ? maxCategoryPagesGenerated
                : currentCategoryPages

            const categoryProfileIndex: number = getProfileIndexFromLanguage(
              c_item_2.category_profiles,
              language,
            )
            const permalink =
              c_item_2.category_profiles[categoryProfileIndex]?.meta_information
                ?.permalink
            for (let i = 1; i <= generateNumberOfCategoryPages; i++) {
              const fullLink = `${parentPermalink_1}/${parentPermalink_2}/${permalink}/${i}`
              const sitemapObject = {
                loc: fullLink, // Absolute url
                lastmod: c_item_2.updatedAt,
                changefreq: 'daily',
                priority: 0.7,
              }
              categoryPermalinks.push(sitemapObject)
            }

            if (c_item_2.child_categories.length > 0) {
              // ==== LEVEL 3 CHILDREN =========
              const childListt_3 = c_item_2.child_categories
              const parentPermalink_3 = permalink
              for (const c_item_3 of childListt_3) {
                // We are looping each item...

                const currentCategoryPages = Math.ceil(
                  c_item_3.product_containers.length / itemsPerPage,
                )
                const generateNumberOfCategoryPages =
                  currentCategoryPages > maxCategoryPagesGenerated
                    ? maxCategoryPagesGenerated
                    : currentCategoryPages

                const categoryProfileIndex: number =
                  getProfileIndexFromLanguage(
                    c_item_3.category_profiles,
                    language,
                  )
                const permalink =
                  c_item_3.category_profiles[categoryProfileIndex]
                    ?.meta_information?.permalink
                for (let i = 1; i <= generateNumberOfCategoryPages; i++) {
                  const fullLink = `${parentPermalink_1}/${parentPermalink_2}/${parentPermalink_3}/${permalink}/${i}`
                  const sitemapObject = {
                    loc: fullLink, // Absolute url
                    lastmod: c_item_3.updatedAt,
                    changefreq: 'daily',
                    priority: 0.7,
                  }
                  categoryPermalinks.push(sitemapObject)
                }

                if (c_item_3.child_categories.length > 0) {
                  // ==== LEVEL 4 CHILDREN =========
                  const childListt_4 = c_item_3.child_categories
                  const parentPermalink_4 = permalink
                  for (const c_item_4 of childListt_4) {
                    // We are looping each item...

                    const currentCategoryPages = Math.ceil(
                      c_item_4.product_containers.length / itemsPerPage,
                    )
                    const generateNumberOfCategoryPages =
                      currentCategoryPages > maxCategoryPagesGenerated
                        ? maxCategoryPagesGenerated
                        : currentCategoryPages

                    const categoryProfileIndex: number =
                      getProfileIndexFromLanguage(
                        c_item_4.category_profiles,
                        language,
                      )
                    const permalink =
                      c_item_4.category_profiles[categoryProfileIndex]
                        ?.meta_information?.permalink
                    for (let i = 1; i <= generateNumberOfCategoryPages; i++) {
                      const fullLink = `${parentPermalink_1}/${parentPermalink_2}/${parentPermalink_3}/${parentPermalink_4}/${permalink}/${i}`
                      const sitemapObject = {
                        loc: fullLink, // Absolute url
                        lastmod: c_item_4.updatedAt,
                        changefreq: 'daily',
                        priority: 0.7,
                      }
                      categoryPermalinks.push(sitemapObject)
                    }

                    if (c_item_4.child_categories.length > 0) {
                      // ==== LEVEL 5 CHILDREN =========
                      const childListt_5 = c_item_4.child_categories
                      const parentPermalink_5 = permalink
                      for (const c_item_5 of childListt_5) {
                        // We are looping each item...

                        const currentCategoryPages = Math.ceil(
                          c_item_5.product_containers.length / itemsPerPage,
                        )
                        const generateNumberOfCategoryPages =
                          currentCategoryPages > maxCategoryPagesGenerated
                            ? maxCategoryPagesGenerated
                            : currentCategoryPages

                        const categoryProfileIndex: number =
                          getProfileIndexFromLanguage(
                            c_item_5.category_profiles,
                            language,
                          )
                        const permalink =
                          c_item_5.category_profiles[categoryProfileIndex]
                            ?.meta_information?.permalink
                        for (
                          let i = 1;
                          i <= generateNumberOfCategoryPages;
                          i++
                        ) {
                          const fullLink = `${parentPermalink_1}/${parentPermalink_2}/${parentPermalink_3}/${parentPermalink_4}/${parentPermalink_5}/${permalink}/${i}`
                          const sitemapObject = {
                            loc: fullLink, // Absolute url
                            lastmod: c_item_5.updatedAt,
                            changefreq: 'daily',
                            priority: 0.7,
                          }
                          categoryPermalinks.push(sitemapObject)
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  let paths = [] as any
  for (const item of categoryPermalinks) {
    // console.log('params array :>> ', item.loc.split('/'))

    const paramObj = {
      params: {
        slug: item.loc.split('/'),
      },
      locale: language,
    }
    paths.push(paramObj)
  }

  // //  TODO This should be OFFF =====
  // paths = []
  // console.log('paths :>> ', paths)

  return {
    paths: paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
  defaultLocale,
}) => {
  const startTime = new Date()
  console.log('starting CATEGORY page getStaticProps')

  const apolloClient = initializeApollo()
  const SHOP_ID = JSON.parse(process.env.NEXT_PUBLIC_SHOP_ID)
  const numberOfProductsToTake: number = 32 //! this have to match the value in server-sitemap.xml

  // === Handle a case, where link is opened without a number at the end...
  // e.g. http://localhost:3000/en/category/sofas-1
  // HAVE IN MIND CHILD CATEGORIES!!!
  // ---------
  const isLastParamNumber = !isNaN(
    parseInt(params.slug[params.slug.length - 1]),
  )

  const paramsFiltered = Array.isArray(params.slug)
    ? params.slug.filter((e) => e !== 'page')
    : params.slug

  let permalink = isLastParamNumber
    ? paramsFiltered[paramsFiltered.length - 2]
    : paramsFiltered[paramsFiltered.length - 1] ?? paramsFiltered[0]
  const pageNumber = isLastParamNumber
    ? paramsFiltered[paramsFiltered.length - 1]
    : 1

  // Fix urls from old website by removing page, from incoming values...
  // permalink = permalink.replace("/page", "");

  // --- We want the permalink to include nested relationships when moving forward (dining-room/sofas/triple-sofas)
  // @ts-ignore
  const permList =
    // @ts-ignore
    paramsFiltered.length > 0 ? [...paramsFiltered] : [paramsFiltered]
  const fullPermalink = isLastParamNumber
    ? permList.slice(0, -1).join('/')
    : permList.join('/')

  // console.log("paramsFiltered :>> ", paramsFiltered);
  // console.log("fullPermalink :>> ", fullPermalink);

  try {
    const staticData = await apolloClient.query({
      query: GET_SORTED_BY_POSITION_PRODUCTS,
      variables: {
        permalink,
        language: locale,
        take: numberOfProductsToTake,
        page: parseInt(pageNumber as string),
      },
    })

    //* ==================================================================================
    //* ==================== Queries of data to pass to page component ===================
    //* ==================================================================================

    const categoriesDataQuery = await apolloClient.query({
      query: GET_PARENT_CATEGORIES_NON_RECURSIVE,
      variables: {
        language: locale,
      },
    })
    let allCategories = [
      ...JSON.parse(JSON.stringify(categoriesDataQuery.data.findManyCategory)),
    ]
    const sortedByMenuOrder = allCategories.sort(
      (a, b) => a.menu_order - b.menu_order,
    )
    const sortedCategories = sortCategories(sortedByMenuOrder)
    // const flatCategories = flattenCategories(sortedCategories)
    let categoriesData = { ...JSON.parse(JSON.stringify(categoriesDataQuery)) }
    categoriesData.data.findManyCategory = sortedCategories
    // ---------------- END  CATEGORY =============

    const childCategoriesData = await apolloClient.query({
      query: GET_CHILD_CATEGORIES,
      variables: {
        permalink: permalink,
      },
    })

    //! ---> Investigate this, why did this have product containers???
    const currentCategoryData = await apolloClient.query({
      query: GET_CATEGORY_DATA,
      variables: {
        permalink: permalink,
      },
    })

    // const timeAfterStaticDatatime =
    //   Math.abs(new Date().getTime() - startTime.getTime()) / 1000
    // console.log('index currentCategoryData ->', timeAfterStaticDatatime)

    const shopSettingsData = await apolloClient.query({
      query: APP_SHOP_SETTINGS,
      variables: {
        SHOP_ID,
        isHomepage: false,
        isProductPage: false,
      },
    })

    // const TimeAftershopSettingsData =
    //   Math.abs(new Date().getTime() - startTime.getTime()) / 1000
    // console.log('index TimeAftershopSettingsData ->', TimeAftershopSettingsData)

    return addApolloState(apolloClient, {
      props: {
        // uaString: req.headers["user-agent"],
        currentCategoryData,
        staticData,
        categoriesData,
        childCategoriesData,
        categoryName:
          currentCategoryData?.data?.findUniqueMetaInformation?.category_profile
            ?.name ?? 'No name',
        categoryDescription:
          currentCategoryData?.data?.findUniqueMetaInformation?.category_profile
            ?.description ?? 'No description',
        permalink: permalink,
        fullPermalink: fullPermalink,
        shopSettingsData,
        pageNumber: parseInt(pageNumber as string),
        locale,
        locales,
        defaultLocale,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 3600, // In seconds  i hr = 3600 12hrs = 43200
    })
  } catch (e) {
    // -----
    console.log('=================================================')
    console.log('Caught error in CATEGORY page GetStaticProps', e)
    return { notFound: true }
  }
}

// export const getServerSideProps: GetServerSideProps = async ({
//   params,
//   locale,
//   locales,
//   defaultLocale,
//   req,
// }) => {
//   const startTime = new Date();
//   console.log("starting CATEGORY page getServerSideProps");

//   const apolloClient = initializeApollo();
//   const SHOP_ID = JSON.parse(process.env.NEXT_PUBLIC_SHOP_ID);
//   const numberOfProductsToTake: number = 32; //! this have to match the value in server-sitemap.xml

//   // === Handle a case, where link is opened without a number at the end...
//   // e.g. http://localhost:3000/en/category/sofas-1
//   // HAVE IN MIND CHILD CATEGORIES!!!
//   // ---------
//   const isLastParamNumber = !isNaN(
//     parseInt(params.slug[params.slug.length - 1])
//   );

//   const paramsFiltered = Array.isArray(params.slug)
//     ? params.slug.filter((e) => e !== "page")
//     : params.slug;

//   let permalink = isLastParamNumber
//     ? paramsFiltered[paramsFiltered.length - 2]
//     : paramsFiltered[paramsFiltered.length - 1] ?? paramsFiltered[0];
//   const pageNumber = isLastParamNumber
//     ? paramsFiltered[paramsFiltered.length - 1]
//     : 1;

//   // Fix urls from old website by removing page, from incoming values...
//   // permalink = permalink.replace("/page", "");

//   // --- We want the permalink to include nested relationships when moving forward (dining-room/sofas/triple-sofas)
//   // @ts-ignore
//   const permList =
//     // @ts-ignore
//     paramsFiltered.length > 0 ? [...paramsFiltered] : [paramsFiltered];
//   const fullPermalink = isLastParamNumber
//     ? permList.slice(0, -1).join("/")
//     : permList.join("/");

//   // console.log("paramsFiltered :>> ", paramsFiltered);
//   // console.log("fullPermalink :>> ", fullPermalink);

//   const staticData = await apolloClient.query({
//     query: GET_SORTED_BY_POSITION_PRODUCTS,
//     variables: {
//       permalink,
//       language: locale,
//       take: numberOfProductsToTake,
//       page: parseInt(pageNumber as string),
//     },
//   });

//   const timeAfterStaticData =
//     Math.abs(new Date().getTime() - startTime.getTime()) / 1000;
//   console.log("index timeAfterStaticData ->", timeAfterStaticData);

//   //* ==================================================================================
//   //* ==================== Queries of data to pass to page component ===================
//   //* ==================================================================================

//   //! ---> Investigate this, why did this have product containers???
//   const currentCategoryData = await apolloClient.query({
//     query: GET_CATEGORY_DATA,
//     variables: {
//       permalink: permalink,
//     },
//   });

//   const timeAfterStaticDatatime =
//     Math.abs(new Date().getTime() - startTime.getTime()) / 1000;
//   console.log("index currentCategoryData ->", timeAfterStaticDatatime);

//   const shopSettingsData = await apolloClient.query({
//     query: APP_SHOP_SETTINGS,
//     variables: {
//       SHOP_ID,
//       isHomepage: false,
//       isProductPage: false,
//     },
//   });

//   const TimeAftershopSettingsData =
//     Math.abs(new Date().getTime() - startTime.getTime()) / 1000;
//   console.log("index TimeAftershopSettingsData ->", TimeAftershopSettingsData);

//   return addApolloState(apolloClient, {
//     props: {
//       uaString: req.headers["user-agent"],
//       currentCategoryData,
//       staticData,
//       categoryName:
//         currentCategoryData?.data?.findUniqueMetaInformation?.category_profile?.name ??
//         null,
//       categoryDescription:
//         currentCategoryData?.data?.findUniqueMetaInformation?.category_profile
//           ?.description ?? null,
//       permalink: permalink,
//       fullPermalink: fullPermalink,
//       shopSettingsData,
//       pageNumber: parseInt(pageNumber as string),
//       locale,
//       locales,
//       defaultLocale,
//       ...(await serverSideTranslations(locale, ["common"])),
//     },
//   });
// };

export default Category
